import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Stack, Card, CardContent, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, Chip, Paper, Divider
} from '@mui/material';
import {
  Download as DownloadIcon, Upload as UploadIcon, Storage as StorageIcon,
  Warning as WarningIcon, CheckCircle as CheckCircleIcon, Refresh as RefreshIcon,
  Shield as ShieldIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from '../api';

const BASE_URL = process.env.REACT_APP_URL || 'http://localhost:5100';

const BackupRestoreDB = () => {
  const navigate = useNavigate();
  const user = useSelector((s) => s.authAdmin?.user);
  const isAdmin = user?.role === 'admin';

  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const notify = (m, s = 'success') => setSnackbar({ open: true, message: m, severity: s });

  const fetchStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/api/backup-restore/status`);
      setStatus(res.data);
    } catch (err) {
      setStatus(null);
      console.error(err);
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // === BACKUP ===
  const handleBackup = async () => {
    setBackupLoading(true);
    try {
      // Ambil CSRF token dari cookie
      const csrfMatch = document.cookie.match(new RegExp('(^| )csrf_token=([^;]+)'));
      const csrfToken = csrfMatch ? csrfMatch[2] : '';

      // Pakai fetch agar bisa menerima blob (file .sql)
      const res = await fetch(`${BASE_URL}/api/backup-restore/backup`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'X-CSRF-Token': csrfToken,
        },
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.msg || `Gagal backup (HTTP ${res.status})`);
      }
      const blob = await res.blob();
      // Buat URL object & trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Ambil nama file dari header Content-Disposition
      const cd = res.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename="?([^"]+)"?/);
      a.download = match ? match[1] : `backup_${Date.now()}.sql`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      notify('Backup database berhasil diunduh');
    } catch (err) {
      console.error(err);
      notify(err.message || 'Gagal backup database', 'error');
    } finally {
      setBackupLoading(false);
    }
  };

  // === RESTORE ===
  const handleFilePick = (e) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      if (!/\.sql$/i.test(file.name)) {
        notify('File harus berekstensi .sql', 'error');
        setRestoreFile(null);
        return;
      }
      if (file.size > 200 * 1024 * 1024) {
        notify('Ukuran file melebihi 200MB', 'error');
        setRestoreFile(null);
        return;
      }
      setRestoreFile(file);
      setConfirmOpen(true);
    }
  };

  const confirmRestore = async () => {
    if (!restoreFile) return;
    setConfirmOpen(false);
    setRestoreLoading(true);
    try {
      const fd = new FormData();
      fd.append('sqlFile', restoreFile);
      const res = await axios.post(`${BASE_URL}/api/backup-restore/restore`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000, // 10 menit
      });
      notify(res.data?.msg || 'Restore database berhasil');
      setRestoreFile(null);
      // Reset input file
      const inp = document.getElementById('restore-sql-input');
      if (inp) inp.value = '';
    } catch (err) {
      console.error(err);
      notify(err.response?.data?.msg || err.message || 'Gagal restore database', 'error');
    } finally {
      setRestoreLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Akses ditolak. Halaman ini khusus Administrator.
        </Alert>
      </Box>
    );
  }

  const toolsReady = status?.available?.mysqldump && status?.available?.mysql;

  return (
    <div className="main-content" style={{ marginLeft: '250px', marginRight: '20px' }}>
      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1100, mx: 'auto' }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              <StorageIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
              Backup & Restore Database
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Kelola cadangan database sistem. Khusus Administrator.
            </Typography>
          </Box>
          <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={fetchStatus} disabled={statusLoading}>
            Cek Status Tools
          </Button>
        </Stack>

        {/* Status tools */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              <ShieldIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
              Status Server
            </Typography>
            {statusLoading ? (
              <CircularProgress size={20} />
            ) : status ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                <Chip
                  size="small"
                  icon={<CheckCircleIcon />}
                  label={`mysqldump: ${status.available?.mysqldump ? 'Tersedia' : 'Tidak ditemukan'}`}
                  color={status.available?.mysqldump ? 'success' : 'error'}
                  variant={status.available?.mysqldump ? 'filled' : 'outlined'}
                />
                <Chip
                  size="small"
                  icon={<CheckCircleIcon />}
                  label={`mysql client: ${status.available?.mysql ? 'Tersedia' : 'Tidak ditemukan'}`}
                  color={status.available?.mysql ? 'success' : 'error'}
                  variant={status.available?.mysql ? 'filled' : 'outlined'}
                />
                {status.database && (
                  <Chip size="small" label={`DB: ${status.database}`} variant="outlined" />
                )}
                {status.host && (
                  <Chip size="small" label={`Host: ${status.host}`} variant="outlined" />
                )}
              </Stack>
            ) : (
              <Alert severity="warning" sx={{ py: 0.5 }}>Tidak dapat memeriksa status tools. Pastikan server berjalan.</Alert>
            )}
            {!statusLoading && status && !toolsReady && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Tools <strong>mysqldump</strong> atau <strong>mysql</strong> tidak ditemukan di server.
                Pastikan MySQL Server / MariaDB terinstal dan path-nya terdaftar di PATH sistem,
                atau berada di lokasi umum (C:\Program Files\MySQL\...\bin).
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Info keamanan */}
        <Alert severity="info" sx={{ mb: 3 }} icon={<ShieldIcon />}>
          <Typography variant="body2">
            <strong>Keamanan:</strong> Hanya admin yang dapat mengakses fitur ini. File backup berisi
            seluruh data database. File restore divalidasi ekstensi, ukuran, dan konten untuk
            mencegah SQL injection. Kredensial DB diambil dari konfigurasi server, bukan input user.
          </Typography>
        </Alert>

        <Divider sx={{ mb: 3 }} />

        {/* Backup Section */}
        <Card variant="outlined" sx={{ mb: 3, border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
              <DownloadIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'success.main' }} />
              Backup Database
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Unduh seluruh data database dalam format .sql. File ini dapat digunakan untuk
              memulihkan data di kemudian hari.
            </Typography>
            <Button
              variant="contained"
              color="success"
              size="large"
              startIcon={backupLoading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
              onClick={handleBackup}
              disabled={backupLoading || (status && !toolsReady)}
            >
              {backupLoading ? 'Memproses Backup...' : 'Download Backup (.sql)'}
            </Button>
          </CardContent>
        </Card>

        {/* Restore Section */}
        <Card variant="outlined" sx={{ mb: 3, border: '2px solid #f57c00' }}>
          <CardContent>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
              <UploadIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'warning.main' }} />
              Restore Database
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Unggah file .sql untuk memulihkan database. <strong>Peringatan:</strong> Operasi ini
              akan menimpa data yang ada saat ini. Pastikan Anda telah membuat backup terlebih dahulu.
            </Typography>

            <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningIcon />}>
              <Typography variant="body2">
                <strong>Perhatian:</strong> Restore akan mengganti seluruh data database dengan isi
                file .sql. Tindakan ini tidak dapat dibatalkan. Disarankan melakukan backup terlebih
                dahulu sebelum restore.
              </Typography>
            </Alert>

            <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ gap: 1 }}>
              <Button
                variant="outlined"
                component="label"
                size="large"
                startIcon={<UploadIcon />}
                disabled={restoreLoading || (status && !toolsReady)}
                sx={{ borderColor: '#f57c00', color: '#f57c00', '&:hover': { borderColor: '#ef6c00', bgcolor: '#fff3e0' } }}
              >
                {restoreLoading ? 'Memproses Restore...' : 'Pilih File .sql'}
                <input
                  id="restore-sql-input"
                  hidden
                  type="file"
                  accept=".sql,application/sql,application/octet-stream,text/plain"
                  onChange={handleFilePick}
                />
              </Button>
              {restoreFile && (
                <Chip
                  label={restoreFile.name}
                  onDelete={() => { setRestoreFile(null); const inp = document.getElementById('restore-sql-input'); if (inp) inp.value = ''; }}
                  color="warning"
                  variant="outlined"
                />
              )}
            </Stack>

            {restoreLoading && (
              <Box sx={{ mt: 2 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Sedang mengunggah & memproses restore... Mohon tunggu.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Dialog Konfirmasi Restore */}
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" /> Konfirmasi Restore Database
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Anda akan melakukan restore database dari file:
            </DialogContentText>
            <Paper variant="outlined" sx={{ p: 1.5, my: 1.5, bgcolor: '#fff3e0' }}>
              <Typography variant="body2" fontWeight={600}>{restoreFile?.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                Ukuran: {restoreFile ? (restoreFile.size / 1024).toFixed(2) : 0} KB
              </Typography>
            </Paper>
            <Alert severity="error" sx={{ mt: 1 }}>
              <Typography variant="body2">
                <strong>PERINGATAN:</strong> Seluruh data database saat ini akan ditimpa!
                Pastikan Anda sudah backup data terbaru. Tindakan ini tidak dapat dibatalkan.
              </Typography>
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)} color="inherit">Batal</Button>
            <Button onClick={confirmRestore} color="error" variant="contained" startIcon={<UploadIcon />}>
              Ya, Restore Sekarang
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        {snackbar.open && (
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar(s => ({ ...s, open: false }))}
            sx={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 1300, boxShadow: 3 }}
          >
            {snackbar.message}
          </Alert>
        )}
      </Box>
    </div>
  );
};

export default BackupRestoreDB;
